export const supportedLocales = ["en", "zh-CN"] as const;

export type Locale = (typeof supportedLocales)[number];
export type TranslationValues = Record<string, string | number | boolean>;
export type TranslationLeaf = string;
export type TranslationNode = TranslationLeaf | TranslationTree;
export interface TranslationTree {
	[key: string]: TranslationNode;
}
export type MessagesByLocale = Record<Locale, TranslationTree>;

export const defaultLocale: Locale = "en";
export const localeLabels: Record<Locale, string> = {
	en: "English",
	"zh-CN": "中文",
};

const localeAliases: Record<string, Locale> = {
	en: "en",
	"en-us": "en",
	en_us: "en",
	zh: "zh-CN",
	"zh-cn": "zh-CN",
	zh_cn: "zh-CN",
	"zh-hans": "zh-CN",
	zh_hans: "zh-CN",
	cn: "zh-CN",
};

export const baseMessages: MessagesByLocale = {
	en: {
		common: {
			loading: "Loading...",
			save: "Save",
			cancel: "Cancel",
			confirm: "Confirm",
			continue: "Continue",
			back: "Back",
			delete: "Delete",
			edit: "Edit",
			create: "Create",
			update: "Update",
			search: "Search",
			close: "Close",
			unknownError: "An unknown error occurred.",
		},
		language: {
			label: "Language",
			english: "English",
			chinese: "Chinese",
		},
		auth: {
			email: "Email",
			password: "Password",
			signIn: "Sign in",
			signUp: "Sign up",
			signOut: "Sign out",
			forgotPassword: "Forgot password?",
			resetPassword: "Reset password",
			continueWithPasskey: "Sign in with passkey",
		},
		navigation: {
			dashboard: "Dashboard",
			billing: "Billing",
			apiKeys: "API Keys",
			providerKeys: "Provider Keys",
			activity: "Activity",
			settings: "Settings",
			playground: "Playground",
			admin: "Admin",
		},
		billing: {
			credits: "Credits",
			topUp: "Top up",
			autoTopUp: "Auto top-up",
			balance: "Balance",
			paymentMethods: "Payment methods",
			transactions: "Transactions",
		},
	},
	"zh-CN": {
		common: {
			loading: "加载中...",
			save: "保存",
			cancel: "取消",
			confirm: "确认",
			continue: "继续",
			back: "返回",
			delete: "删除",
			edit: "编辑",
			create: "创建",
			update: "更新",
			search: "搜索",
			close: "关闭",
			unknownError: "发生未知错误。",
		},
		language: {
			label: "语言",
			english: "英文",
			chinese: "中文",
		},
		auth: {
			email: "邮箱",
			password: "密码",
			signIn: "登录",
			signUp: "注册",
			signOut: "退出登录",
			forgotPassword: "忘记密码？",
			resetPassword: "重置密码",
			continueWithPasskey: "使用通行密钥登录",
		},
		navigation: {
			dashboard: "控制台",
			billing: "账单",
			apiKeys: "API 密钥",
			providerKeys: "模型供应商密钥",
			activity: "调用记录",
			settings: "设置",
			playground: "调试台",
			admin: "管理后台",
		},
		billing: {
			credits: "额度",
			topUp: "充值",
			autoTopUp: "自动充值",
			balance: "余额",
			paymentMethods: "支付方式",
			transactions: "交易记录",
		},
	},
};

export function normalizeLocale(locale: string | null | undefined): Locale {
	if (!locale) {
		return defaultLocale;
	}

	const normalized = locale.trim().toLowerCase();
	return localeAliases[normalized] ?? defaultLocale;
}

export function getBrowserLocale(languages: readonly string[] = []): Locale {
	for (const language of languages) {
		const locale = normalizeLocale(language);
		if (locale !== defaultLocale || language.toLowerCase().startsWith("en")) {
			return locale;
		}
	}
	return defaultLocale;
}

export function mergeMessages(
	base: MessagesByLocale,
	overrides?: Partial<Record<Locale, TranslationTree>>,
): MessagesByLocale {
	if (!overrides) {
		return base;
	}

	return {
		en: mergeTree(base.en, overrides.en),
		"zh-CN": mergeTree(base["zh-CN"], overrides["zh-CN"]),
	};
}

export function createTranslator(
	locale: Locale,
	messages: MessagesByLocale = baseMessages,
) {
	return (key: string, values?: TranslationValues) =>
		translate(messages, locale, key, values);
}

export function translate(
	messages: MessagesByLocale,
	locale: Locale,
	key: string,
	values?: TranslationValues,
): string {
	const message =
		readMessage(messages[locale], key) ??
		readMessage(messages[defaultLocale], key) ??
		key;

	return interpolate(message, values);
}

function mergeTree(
	base: TranslationTree,
	override: TranslationTree | undefined,
): TranslationTree {
	if (!override) {
		return base;
	}

	const merged: TranslationTree = { ...base };
	for (const [key, value] of Object.entries(override)) {
		const baseValue = merged[key];
		if (isTree(baseValue) && isTree(value)) {
			merged[key] = mergeTree(baseValue, value);
		} else {
			merged[key] = value;
		}
	}
	return merged;
}

function readMessage(tree: TranslationTree, key: string): string | undefined {
	let current: TranslationNode | undefined = tree;
	for (const segment of key.split(".")) {
		if (!isTree(current)) {
			return undefined;
		}
		current = current[segment];
	}
	return typeof current === "string" ? current : undefined;
}

function interpolate(message: string, values?: TranslationValues): string {
	if (!values) {
		return message;
	}

	return message.replace(/\{(\w+)\}/g, (match, key: string) =>
		Object.hasOwn(values, key) ? String(values[key]) : match,
	);
}

function isTree(value: TranslationNode | undefined): value is TranslationTree {
	return typeof value === "object" && value !== null;
}
